/**
 * Light / dark theme. The preference (`ol.theme` in localStorage: 'light' | 'dark'; absent = follow
 * the system) is turned into `data-theme` on <html>, which is all the stylesheet looks at (the
 * tokens in styles.css). Imported first by main.tsx so the attribute is set before the first paint.
 */
import { useEffect, useState } from 'preact/hooks';
import { getPrefs, subscribePrefs, type Prefs } from '../prefs';

export type Theme = 'light' | 'dark';
export type ThemePref = Theme | 'system';

const KEY = 'ol.theme';
let externalTheme = false;
/** An embedded host can own the theme instead of the browser preference. */
export function useHostTheme(): void { externalTheme = true; }
const media = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
const listeners = new Set<() => void>();
let pref: ThemePref = load();

function load(): ThemePref {
  try { const v = localStorage.getItem(KEY); return v === 'light' || v === 'dark' ? v : 'system'; } catch { return 'system'; }
}

export function themePref(): ThemePref { return pref; }
/** the theme actually shown */
export function currentTheme(): Theme { return pref === 'system' ? (media?.matches ? 'dark' : 'light') : pref; }

export function setThemePref(p: ThemePref): void {
  pref = p;
  try { if (p === 'system') localStorage.removeItem(KEY); else localStorage.setItem(KEY, p); } catch { /* ignore */ }
  apply();
}

function apply(): void {
  if (externalTheme) return;
  const t = currentTheme();
  document.documentElement.dataset.theme = t;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', t === 'dark' ? '#1c1c21' : '#3b6ea5');
  listeners.forEach(l => l());
}

media?.addEventListener('change', () => { if (pref === 'system') apply(); });
apply();

/**
 * The dark theme's text tone (prefs.darkTone, chosen in the sun/moon switch's menu): white, or —
 * like Apple Books' sepia and grey reading themes — a warmer or softer tone for everything that is
 * white on the near-black page (text, formulas, the caret). `data-tone` on <html>; styles.css redefines
 * --editor-fg and --caret for it, so nothing but those tokens knows about it. Both shells import this
 * module, so both get it.
 */
export type DarkTone = Prefs['darkTone'];
export const DARK_TONES: { id: DarkTone; label: string }[] = [{ id: 'white', label: 'White' }, { id: 'sepia', label: 'Sepia' }, { id: 'gray', label: 'Gray' }];
function applyTone(p: Prefs): void { document.documentElement.dataset.tone = p.darkTone; }
subscribePrefs(applyTone);
applyTone(getPrefs());

/**
 * Smart invert (prefs.darkPages, prefs.invertFigures — app/darkpages.ts): `data-dark-pages` and
 * `data-invert-figures` on <html>, which styles.css combines with `data-theme` — the editor's page
 * dark or white paper, figures and layout pages inverted or not
 */
function applyDarkPages(p: Prefs): void {
  const el = document.documentElement;
  el.toggleAttribute('data-dark-pages', !!p.darkPages);
  el.toggleAttribute('data-invert-figures', !!p.invertFigures);
}
subscribePrefs(applyDarkPages);
applyDarkPages(getPrefs());

/** Preact hook: re-renders when the preference or the system theme changes. */
export function useTheme(): { pref: ThemePref; theme: Theme } {
  const [, tick] = useState(0);
  useEffect(() => { const l = () => tick(n => n + 1); listeners.add(l); return () => { listeners.delete(l); }; }, []);
  return { pref, theme: currentTheme() };
}
